import {getBaseUrl, parseRegion} from '~/lib/config';
import {
  AccessDeniedError,
  ApiError,
  NotFoundError,
  RateLimitedError,
} from './errors';
import type {OrganizationInfo, RawOrganizationInfo} from './types';
import {normalizeOrganization} from './types';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: Record<string, unknown>;
}

export async function request<T>(
  identifier: string,
  path: string,
  options?: RequestOptions,
): Promise<T> {
  const url = `${getBaseUrl(parseRegion(identifier))}${path}`;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options?.headers as Record<string, string> | undefined),
  };

  let body: string | undefined;
  if (options?.body) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const response = await fetch(url, {
    ...options,
    headers,
    body,
    credentials: 'omit',
  });

  if (!response.ok) {
    let code: string | undefined;
    let organization: OrganizationInfo | undefined;
    try {
      const body = (await response.json()) as {
        error?: string;
        code?: string;
        organization?: RawOrganizationInfo;
      };
      code = body.code ?? body.error;
      organization = normalizeOrganization(body.organization);
    } catch {
      // ignore parse failures
    }

    if (response.status === 404) {
      throw new NotFoundError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError(organization);
    }
    if (response.status === 429) {
      throw new RateLimitedError();
    }

    throw new ApiError(response.status, response.statusText, code);
  }

  return (await response.json()) as T;
}
